import React, { useEffect, useState } from 'react';
import {
  FiShield, FiSave, FiTrash2, FiCheckCircle, FiAlertCircle,
  FiExternalLink, FiRefreshCw, FiInfo,
} from 'react-icons/fi';
import FeatureGate from '../common/FeatureGate';
import {
  getSSOConfig, saveSSOConfig, deleteSSOConfig, testSSOConfig,
  getSAMLMetadataURL, type SSOConfig,
} from '../../api/sso';
import { useAuth } from '../../context/AuthContext';

const DEFAULT_CONFIG: SSOConfig = {
  protocol: 'saml',
  domain: '',
  idp_entity_id: '',
  idp_sso_url: '',
  idp_certificate: '',
  sp_entity_id: '',
  client_id: '',
  client_secret: '',
  discovery_url: '',
  scopes: 'openid email profile',
  attribute_mapping: { email: 'email', name: 'name' },
  auto_provision: true,
  default_role: 'member',
  is_active: true,
};

const SSOPage: React.FC = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState<SSOConfig | null>(null);
  const [form, setForm] = useState<SSOConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message?: string; issues?: string[] } | null>(null);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    getSSOConfig()
      .then(c => {
        if (c) { setConfig(c); setForm({ ...DEFAULT_CONFIG, ...c }); }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function set<K extends keyof SSOConfig>(k: K, v: SSOConfig[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true); setError(null); setSaveMsg(null);
    try {
      const saved = await saveSSOConfig(form);
      setConfig(saved);
      setForm({ ...DEFAULT_CONFIG, ...saved });
      setSaveMsg('Configuration SSO sauvegardée avec succès.');
      setTimeout(() => setSaveMsg(null), 4000);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true); setTestResult(null);
    try {
      const r = await testSSOConfig();
      setTestResult(r);
    } catch {
      setTestResult({ success: false, issues: ['Erreur lors du test'] });
    } finally {
      setTesting(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true);
    try {
      await deleteSSOConfig();
      setConfig(null);
      setForm(DEFAULT_CONFIG);
      setConfirmDelete(false);
      setSaveMsg('Configuration SSO supprimée.');
      setTimeout(() => setSaveMsg(null), 3000);
    } catch {
      setError('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  }

  const orgId = user?.organization_id || '';
  const metadataUrl = orgId ? getSAMLMetadataURL(orgId) : '';

  return (
    <FeatureGate feature="sso" mode="page" lockedMessage="Single Sign-On (SSO)">
      <div className="p-6 max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
            <FiShield size={20} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Single Sign-On (SSO)</h1>
            <p className="text-sm text-gray-500">Configurez SAML 2.0 ou OIDC pour votre organisation</p>
          </div>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded-lg" />
            ))}
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-6">

            {/* Protocol + Domain */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
              <h2 className="font-semibold text-gray-800">Configuration générale</h2>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Protocole</label>
                  <select
                    value={form.protocol}
                    onChange={e => set('protocol', e.target.value as 'saml' | 'oidc')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="saml">SAML 2.0</option>
                    <option value="oidc">OpenID Connect (OIDC)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Domaine email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="company.com"
                    value={form.domain}
                    onChange={e => set('domain', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Les utilisateurs avec ce domaine seront redirigés vers votre IdP
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between py-2 border-t border-gray-100">
                <div>
                  <p className="text-sm font-medium text-gray-700">SSO actif</p>
                  <p className="text-xs text-gray-400">Désactivez pour basculer en connexion classique</p>
                </div>
                <button
                  type="button"
                  onClick={() => set('is_active', !form.is_active)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    form.is_active ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                    form.is_active ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>

            {/* SAML Fields */}
            {form.protocol === 'saml' && (
              <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-gray-800">Configuration SAML 2.0</h2>
                  {orgId && (
                    <a
                      href={metadataUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                    >
                      Métadonnées SP <FiExternalLink size={11} />
                    </a>
                  )}
                </div>

                <Field label="Entity ID de l'IdP" required
                  placeholder="https://idp.company.com/issuer"
                  value={form.idp_entity_id || ''}
                  onChange={v => set('idp_entity_id', v)}
                />
                <Field label="URL SSO de l'IdP" required
                  placeholder="https://idp.company.com/sso/saml"
                  value={form.idp_sso_url || ''}
                  onChange={v => set('idp_sso_url', v)}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Certificat X.509 de l'IdP <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={5}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;MIIC...&#10;-----END CERTIFICATE-----"
                    value={form.idp_certificate || ''}
                    onChange={e => set('idp_certificate', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <Field label="Entity ID du SP (optionnel)"
                  placeholder={`${window.location.origin}/api/sso/saml/metadata/${orgId}`}
                  value={form.sp_entity_id || ''}
                  onChange={v => set('sp_entity_id', v)}
                />

                <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
                  <FiInfo size={14} className="mt-0.5 flex-shrink-0" />
                  <div>
                    URL ACS (callback) : <code className="font-mono bg-blue-100 px-1 rounded">
                      {`http://localhost:5000/api/sso/saml/callback`}
                    </code>
                  </div>
                </div>
              </div>
            )}

            {/* OIDC Fields */}
            {form.protocol === 'oidc' && (
              <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
                <h2 className="font-semibold text-gray-800">Configuration OpenID Connect</h2>

                <Field label="Discovery URL" required
                  placeholder="https://login.microsoftonline.com/{tenant}/v2.0"
                  value={form.discovery_url || ''}
                  onChange={v => set('discovery_url', v)}
                />
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Client ID" required
                    placeholder="your-client-id"
                    value={form.client_id || ''}
                    onChange={v => set('client_id', v)}
                  />
                  <Field label="Client Secret" required type="password"
                    placeholder="••••••••"
                    value={form.client_secret || ''}
                    onChange={v => set('client_secret', v)}
                  />
                </div>
                <Field label="Scopes"
                  placeholder="openid email profile"
                  value={form.scopes || 'openid email profile'}
                  onChange={v => set('scopes', v)}
                />

                <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
                  <FiInfo size={14} className="mt-0.5 flex-shrink-0" />
                  <div>
                    Redirect URI : <code className="font-mono bg-blue-100 px-1 rounded">
                      {`http://localhost:5000/api/sso/oidc/callback`}
                    </code>
                  </div>
                </div>
              </div>
            )}

            {/* Attribute Mapping */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
              <h2 className="font-semibold text-gray-800">Mapping d'attributs</h2>
              <p className="text-xs text-gray-500">
                {form.protocol === 'saml'
                  ? 'Noms des attributs dans l\'assertion SAML (ou chemins namespace ex: http://schemas.xmlsoap.org/.../emailaddress)'
                  : 'Champs du document userinfo OIDC'}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Attribut email"
                  value={form.attribute_mapping?.email || 'email'}
                  onChange={v => set('attribute_mapping', { ...form.attribute_mapping!, email: v })}
                />
                <Field label="Attribut nom complet"
                  value={form.attribute_mapping?.name || 'name'}
                  onChange={v => set('attribute_mapping', { ...form.attribute_mapping!, name: v })}
                />
              </div>
            </div>

            {/* Provisioning */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
              <h2 className="font-semibold text-gray-800">Provisioning automatique</h2>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Créer les comptes automatiquement</p>
                  <p className="text-xs text-gray-400">
                    Si désactivé, l'utilisateur doit exister avant de se connecter via SSO
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => set('auto_provision', !form.auto_provision)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    form.auto_provision ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                    form.auto_provision ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rôle par défaut des nouveaux utilisateurs SSO
                </label>
                <select
                  value={form.default_role}
                  onChange={e => set('default_role', e.target.value as 'member' | 'admin')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="member">Membre</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
            </div>

            {/* Feedback */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <FiAlertCircle size={16} /> {error}
              </div>
            )}
            {saveMsg && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                <FiCheckCircle size={16} /> {saveMsg}
              </div>
            )}
            {testResult && (
              <div className={`p-3 border rounded-lg text-sm ${
                testResult.success
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-amber-50 border-amber-200 text-amber-800'
              }`}>
                {testResult.success ? (
                  <div className="flex items-center gap-2">
                    <FiCheckCircle size={16} /> {testResult.message || 'Configuration valide'}
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FiAlertCircle size={16} /> Problèmes détectés :
                    </div>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      {testResult.issues?.map((i, idx) => <li key={idx}>{i}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-2">
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? <FiRefreshCw size={14} className="animate-spin" /> : <FiSave size={14} />}
                  Sauvegarder
                </button>
                {config && (
                  <button
                    type="button"
                    disabled={testing}
                    onClick={handleTest}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                  >
                    {testing ? <FiRefreshCw size={14} className="animate-spin" /> : <FiCheckCircle size={14} />}
                    Tester
                  </button>
                )}
              </div>
              {config && (
                <button
                  type="button"
                  disabled={deleting}
                  onClick={handleDelete}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    confirmDelete
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'border border-red-300 text-red-600 hover:bg-red-50'
                  } disabled:opacity-50`}
                >
                  <FiTrash2 size={14} />
                  {confirmDelete ? 'Confirmer la suppression' : 'Supprimer'}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </FeatureGate>
  );
};

// Composant champ générique
function Field({
  label, value, onChange, placeholder, required, type = 'text',
}: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; required?: boolean; type?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
}

export default SSOPage;
